import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Upload, User, Building, Mail, Phone, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BusinessCards({ eventId, eventName }) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCard, setEditingCard] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    company: '',
    title: '',
    email: '',
    phone: '',
    social_media: '',
    website: '',
    notes: '',
    photo_url: '',
    met_at_convention: eventName || '',
  });

  useEffect(() => {
    fetchCards();
  }, [eventId]);

  const fetchCards = async () => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const response = await axios.get(`${BACKEND_URL}/api/business-cards?event_id=${eventId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCards(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('vyrus_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const payload = {
        ...formData,
        event_id: eventId,
        met_at_convention: formData.met_at_convention || eventName,
      };

      if (editingCard) {
        await axios.put(`${BACKEND_URL}/api/business-cards/${editingCard.id}`, payload, { headers });
        toast.success('Contact updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/business-cards`, payload, { headers });
        toast.success('Contact added');
      }
      
      setDialogOpen(false);
      setEditingCard(null);
      resetForm();
      fetchCards();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save contact');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      const token = localStorage.getItem('vyrus_token');
      await axios.delete(`${BACKEND_URL}/api/business-cards/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Contact deleted');
      fetchCards();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const openEdit = (card) => {
    setEditingCard(card);
    setFormData({
      name: card.name,
      company: card.company || '',
      title: card.title || '',
      email: card.email || '',
      phone: card.phone || '',
      social_media: card.social_media || '',
      website: card.website || '',
      notes: card.notes || '',
      photo_url: card.photo_url || '',
      met_at_convention: card.met_at_convention || eventName || '',
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      company: '',
      title: '',
      email: '',
      phone: '',
      social_media: '',
      website: '',
      notes: '',
      photo_url: '',
      met_at_convention: eventName || '',
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // For now, just convert to base64 data URL
    const reader = new FileReader();
    reader.onload = (event) => {
      setFormData({ ...formData, photo_url: event.target.result });
      toast.success('Photo uploaded');
    };
    reader.readAsDataURL(file);
  };

  if (loading) {
    return <div className="text-muted-foreground text-center py-8">Loading contacts...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold font-heading">Business Cards & Contacts</h3>
        <Button size="sm" onClick={() => { resetForm(); setEditingCard(null); setDialogOpen(true); }} data-testid="add-contact-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Contact
        </Button>
      </div>

      {cards.length === 0 ? (
        <Card className="p-8 text-center bg-card border-border/50">
          <User className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground mb-3">No contacts collected yet</p>
          <Button size="sm" onClick={() => { resetForm(); setDialogOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Contact
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {cards.map((card) => (
            <Card key={card.id} className="bg-card border-border/50" data-testid={`contact-${card.id}`}>
              <CardContent className="p-4">
                <div className="flex gap-4">
                  {card.photo_url ? (
                    <img
                      src={card.photo_url}
                      alt={card.name}
                      className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-secondary/50 flex items-center justify-center flex-shrink-0">
                      <User className="h-8 w-8 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold truncate">{card.name}</h4>
                    {card.title && <p className="text-sm text-muted-foreground">{card.title}</p>}
                    {card.company && (
                      <p className="text-sm text-primary flex items-center gap-1">
                        <Building className="h-3 w-3" />
                        {card.company}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2 mt-2 text-xs">
                      {card.email && (
                        <a href={`mailto:${card.email}`} className="flex items-center gap-1 text-muted-foreground hover:text-primary">
                          <Mail className="h-3 w-3" />
                          Email
                        </a>
                      )}
                      {card.phone && (
                        <a href={`tel:${card.phone}`} className="flex items-center gap-1 text-muted-foreground hover:text-primary">
                          <Phone className="h-3 w-3" />
                          Call
                        </a>
                      )}
                      {card.website && (
                        <a href={card.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-muted-foreground hover:text-primary">
                          <Globe className="h-3 w-3" />
                          Website
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(card)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDelete(card.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {card.notes && (
                  <p className="text-sm text-muted-foreground mt-3 pt-3 border-t border-border/50">{card.notes}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Contact Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditingCard(null); resetForm(); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-card">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading">
              {editingCard ? 'Edit Contact' : 'Add Contact'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 pt-4">
            {/* Photo Upload */}
            <div>
              <Label>Business Card Photo (Optional)</Label>
              <div className="flex items-center gap-4 mt-2">
                {formData.photo_url ? (
                  <img src={formData.photo_url} alt="Preview" className="w-20 h-20 rounded-lg object-cover" />
                ) : (
                  <div className="w-20 h-20 rounded-lg bg-secondary/50 flex items-center justify-center">
                    <Upload className="h-6 w-6 text-muted-foreground" />
                  </div>
                )}
                <div>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="photo-upload"
                  />
                  <label htmlFor="photo-upload">
                    <Button type="button" variant="outline" size="sm" asChild>
                      <span>Upload Photo</span>
                    </Button>
                  </label>
                  {formData.photo_url && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="ml-2 text-destructive"
                      onClick={() => setFormData({ ...formData, photo_url: '' })}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-secondary/50 border-transparent focus:border-primary"
                />
              </div>
              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Artist, Manager"
                  className="bg-secondary/50 border-transparent focus:border-primary"
                />
              </div>
              <div>
                <Label htmlFor="company">Company/Business</Label>
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="bg-secondary/50 border-transparent focus:border-primary"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-secondary/50 border-transparent focus:border-primary"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-secondary/50 border-transparent focus:border-primary"
                />
              </div>
              <div>
                <Label htmlFor="social_media">Social Media</Label>
                <Input
                  id="social_media"
                  value={formData.social_media}
                  onChange={(e) => setFormData({ ...formData, social_media: e.target.value })}
                  placeholder="@handle or URL"
                  className="bg-secondary/50 border-transparent focus:border-primary"
                />
              </div>
              <div>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://"
                  className="bg-secondary/50 border-transparent focus:border-primary"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="met_at_convention">Met At Convention</Label>
                <Input
                  id="met_at_convention"
                  value={formData.met_at_convention}
                  onChange={(e) => setFormData({ ...formData, met_at_convention: e.target.value })}
                  className="bg-secondary/50 border-transparent focus:border-primary"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="What do they do? What were you discussing?"
                  className="bg-secondary/50 border-transparent focus:border-primary min-h-[80px]"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit">{editingCard ? 'Update' : 'Add'} Contact</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
